// src/components/JobSection.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, Upload, Select, notification, Table, Empty } from 'antd';
import { useDispatch } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons';
import { createJob } from '../actions/jobActions';
import { fetchComponents } from '../actions/componentActions';
import './JobSection.css';

const { Option } = Select;

const JobSection = ({ applicationId, components, setIsJobsSubmitted }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // which component the job maps to
  const [targetComponentId, setTargetComponentId] = useState(null);
  const [manifestFile, setManifestFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [jobData, setJobData] = useState({
    executionTime: '',
    frequency: '',
    cpu: '',
    memory: '',
    serviceName: '',
  });

  // Guard selection if components change (e.g., a component gets deleted)
  useEffect(() => {
    if (!components?.length) return;
    if (!targetComponentId || !components.some(c => c.id === targetComponentId)) {
      setTargetComponentId(components[0].id);
    }
  }, [components, targetComponentId]);

  // Mark step as done if any component already has jobs
  useEffect(() => {
    const hasAnyJobs = (components || []).some(
      c => Array.isArray(c.jobs) && c.jobs.length > 0
    );
    if (hasAnyJobs) setIsJobsSubmitted?.(true);
  }, [components, setIsJobsSubmitted]);

  const selectedComponent = useMemo(
    () => (components || []).find(c => c.id === targetComponentId),
    [components, targetComponentId]
  );
  const selectedJobs = selectedComponent?.jobs || [];

  const beforeUpload = (file) => {
    const name = (file?.name || '').toLowerCase();
    const ok = name.endsWith('.yml') || name.endsWith('.yaml');
    if (!ok) {
      notification.warning({
        message: 'Invalid file',
        description: 'Please upload a .yml or .yaml manifest.',
      });
    }
    // prevent auto upload; keep file client-side
    return ok ? false : Upload.LIST_IGNORE;
  };

  const handleFileChange = ({ file }) => {
    if (!file) return;
    const name = (file?.name || '').toLowerCase();
    if (name.endsWith('.yml') || name.endsWith('.yaml')) {
      setManifestFile(file);
    } else {
      setManifestFile(null);
    }
  };

  const handleChange = (field, value) => {
    setJobData(prev => ({ ...prev, [field]: value }));
  };

  const appendIfPresent = (fd, key, val) => {
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      fd.append(key, val);
    }
  };

  const handleJobSubmit = async () => {
    if (!targetComponentId) {
      notification.warning({
        message: 'Select Component',
        description: 'Please choose the component to map this job to.',
      });
      return;
    }
    if (!manifestFile) {
      notification.warning({
        message: 'Manifest Required',
        description: 'Please upload a .yml/.yaml manifest.',
      });
      return;
    }
    if (submitting) return;

    const formData = new FormData();
    formData.append('manifest', manifestFile);

    // Only send fields that are provided
    appendIfPresent(formData, 'executionTime', jobData.executionTime);
    appendIfPresent(formData, 'frequency', jobData.frequency);
    appendIfPresent(formData, 'cpu', jobData.cpu);
    appendIfPresent(formData, 'memory', jobData.memory);
    appendIfPresent(formData, 'serviceName', jobData.serviceName);

    try {
      setSubmitting(true);
      await dispatch(createJob({ formData, componentId: targetComponentId })).unwrap();

      notification.success({
        message: 'Job Added Successfully',
        description: 'The job has been added to the selected component.',
      });

      // Refresh components to get fresh jobs from backend
      await dispatch(fetchComponents(applicationId));

      // Mark step as satisfied
      setIsJobsSubmitted?.(true);

      // Reset ONLY the job fields (keep the selected target component!)
      setJobData({
        executionTime: '',
        frequency: '',
        cpu: '',
        memory: '',
        serviceName: '',
      });
      setManifestFile(null);
      form.resetFields(['executionTime', 'frequency', 'cpu', 'memory', 'serviceName']);
    } catch (e) {
      notification.error({
        message: 'Error Adding Job',
        description: e?.message || 'There was an error adding the job. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Service',
      dataIndex: 'serviceName',
      key: 'serviceName',
      render: (v, r) => v ?? r?.name ?? '-',
    },
    {
      title: 'Execution Time',
      dataIndex: 'executionTime',
      key: 'executionTime',
      render: (v) => (v === undefined || v === null || v === '' ? '-' : v),
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (v) => (v === undefined || v === null || v === '' ? '-' : v),
    },
    {
      title: 'CPU',
      dataIndex: 'cpu',
      key: 'cpu',
      render: (v) => (v === undefined || v === null || v === '' ? '-' : v),
    },
    {
      title: 'Memory',
      dataIndex: 'memory',
      key: 'memory',
      render: (v) => (v === undefined || v === null || v === '' ? '-' : v),
    },
    {
      title: 'Manifest',
      dataIndex: 'manifest',
      key: 'manifest',
      render: (_, r) => r?.manifestName || r?.fileName || r?.manifest || '-',
    },
  ];

  return (
    <div className="job-section">
      <h5>Jobs</h5>

      {/* Keep this OUTSIDE of form's controlled fields so it doesn't reset */}
      <div className="form-item">
        <label>Target Component</label>
        <Select
          placeholder="Select component to map this job to"
          onChange={setTargetComponentId}
          value={targetComponentId}
          style={{ width: '100%' }}
          disabled={!components?.length}
        >
          {(components || []).map((c) => (
            <Option key={c.id} value={c.id}>
              {c.name}
            </Option>
          ))}
        </Select>
      </div>

      <Form layout="vertical" onFinish={handleJobSubmit} form={form}>
        <Form.Item label="Manifest (.yml/.yaml)" required>
          <Upload
            beforeUpload={beforeUpload}
            maxCount={1}
            fileList={manifestFile ? [manifestFile] : []}
            onChange={handleFileChange}
            accept=".yml,.yaml"
          >
            <Button icon={<UploadOutlined />}>Upload Manifest</Button>
          </Upload>
        </Form.Item>

        {/* All optional now */}
        <Form.Item label="Execution Time" name="executionTime">
          <Input
            value={jobData.executionTime}
            onChange={(e) => handleChange('executionTime', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Frequency" name="frequency">
          <Input
            value={jobData.frequency}
            onChange={(e) => handleChange('frequency', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="CPU" name="cpu">
          <Input value={jobData.cpu} onChange={(e) => handleChange('cpu', e.target.value)} />
        </Form.Item>

        <Form.Item label="Memory" name="memory">
          <Input value={jobData.memory} onChange={(e) => handleChange('memory', e.target.value)} />
        </Form.Item>

        <Form.Item label="Service Name" name="serviceName">
          <Input
            value={jobData.serviceName}
            onChange={(e) => handleChange('serviceName', e.target.value)}
          />
        </Form.Item>

        <div className="job-submit-row">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Add Job
          </Button>
        </div>
      </Form>

      {/* Jobs table (optional; keep commented if not needed) */}
      {/* <div style={{ marginTop: 16 }}>
        {selectedJobs.length ? (
          <Table
            size="small"
            rowKey={(r, i) => r.id ?? r._id ?? `${i}`}
            columns={columns}
            dataSource={selectedJobs}
            pagination={{ pageSize: 5 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No jobs for this component yet."
          />
        )}
      </div> */}
    </div>
  );
};

export default JobSection;
